import { useEffect, useMemo, useRef, useState } from 'react';

import { RADAR_AXES } from '../../../mocks/radarNormalization';
import { LuminaireAnalytics } from '../../../types/analytics';

interface SlotData {
  label: string;
  analytics: LuminaireAnalytics;
  color: string;
  stroke: 'solid' | 'dashed';
}

interface RadarChartProps {
  slotA: SlotData | null;
  slotB: SlotData | null;
  size?: number;
  onAxisClick?: (axisKey: string) => void;
}

const RINGS = [0.2, 0.4, 0.6, 0.8, 1.0];
const ANIMATION_MS = 400;
// Derived from RADAR_AXES so the geometry/labels stay in sync if axes change.
const AXIS_COUNT = RADAR_AXES.length;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const polarToXY = (cx: number, cy: number, radius: number, angle: number): [number, number] => [
  cx + radius * Math.cos(angle),
  cy + radius * Math.sin(angle),
];

const buildPoints = (values: number[], cx: number, cy: number, r: number): [number, number][] =>
  values.map((v, i) => {
    const angle = -Math.PI / 2 + (i / AXIS_COUNT) * Math.PI * 2;
    return polarToXY(cx, cy, (Math.max(0, Math.min(100, v)) / 100) * r, angle);
  });

const pointsToString = (pts: [number, number][]): string =>
  pts.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');

/** Animates between two value vectors over ANIMATION_MS using rAF. */
const useAnimatedValues = (target: number[]): number[] => {
  const [current, setCurrent] = useState<number[]>(target);
  const fromRef = useRef<number[]>(target);
  const toRef = useRef<number[]>(target);
  const startRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (current.length === target.length && current.every((v, i) => v === target[i])) return;

    fromRef.current = current;
    toRef.current = target;
    startRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / ANIMATION_MS);
      const eased = easeInOutCubic(t);
      const next = toRef.current.map((to, i) => {
        const from = fromRef.current[i] ?? to;
        return from + (to - from) * eased;
      });
      setCurrent(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.join(',')]);

  return current;
};

export const RadarChart = ({ slotA, slotB, size = 540, onAxisClick }: RadarChartProps) => {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.36;
  const labelRadius = r + 42;

  const targetA = useMemo(
    () => (slotA ? RADAR_AXES.map((axis) => axis.normalize(slotA.analytics)) : new Array(AXIS_COUNT).fill(0)),
    [slotA],
  );
  const targetB = useMemo(
    () => (slotB ? RADAR_AXES.map((axis) => axis.normalize(slotB.analytics)) : new Array(AXIS_COUNT).fill(0)),
    [slotB],
  );

  const animA = useAnimatedValues(targetA);
  const animB = useAnimatedValues(targetB);

  const ptsA = buildPoints(animA, cx, cy, r);
  const ptsB = buildPoints(animB, cx, cy, r);

  const [hovered, setHovered] = useState<{ axis: number; slot: 'A' | 'B' } | null>(null);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="overflow-visible">
        {/* Concentric polygon grid rings */}
        {RINGS.map((ringScale, ringIdx) => {
          const ringPts: [number, number][] = [];
          for (let i = 0; i < AXIS_COUNT; i++) {
            const angle = -Math.PI / 2 + (i / AXIS_COUNT) * Math.PI * 2;
            ringPts.push(polarToXY(cx, cy, r * ringScale, angle));
          }
          return (
            <polygon
              key={ringIdx}
              points={pointsToString(ringPts)}
              fill="rgba(255,255,255,0.015)"
              stroke="#2A2A33"
              strokeWidth={1}
            />
          );
        })}

        {/* Axis lines */}
        {RADAR_AXES.map((_, i) => {
          const angle = -Math.PI / 2 + (i / AXIS_COUNT) * Math.PI * 2;
          const [x, y] = polarToXY(cx, cy, r, angle);
          return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="#2A2A33" strokeWidth={1} />;
        })}

        {/* Slot B polygon — OUTLINED treatment (dashed stroke, no fill) so it
            stays visually distinct from Slot A while sharing the same hue. */}
        {slotB && (
          <polygon
            points={pointsToString(ptsB)}
            fill="none"
            stroke={slotB.color}
            strokeWidth={2}
            strokeDasharray="6 4"
          />
        )}

        {/* Slot A polygon — FILLED treatment */}
        {slotA && (
          <polygon
            points={pointsToString(ptsA)}
            fill={slotA.color}
            fillOpacity={0.25}
            stroke={slotA.color}
            strokeWidth={2}
          />
        )}

        {/* Vertices — Slot A solid disc, Slot B hollow ring */}
        {slotB &&
          ptsB.map(([x, y], i) => {
            const r = hovered?.axis === i && hovered?.slot === 'B' ? 6 : 4;
            return (
              <circle
                key={`vb-${i}`}
                cx={x}
                cy={y}
                r={r}
                fill="#0E0E12"
                stroke={slotB.color}
                strokeWidth={2}
                style={{ cursor: 'pointer', transition: 'r 150ms ease' }}
                onMouseEnter={() => setHovered({ axis: i, slot: 'B' })}
                onMouseLeave={() => setHovered(null)}
              />
            );
          })}
        {slotA &&
          ptsA.map(([x, y], i) => (
            <circle
              key={`va-${i}`}
              cx={x}
              cy={y}
              r={hovered?.axis === i && hovered?.slot === 'A' ? 6 : 4}
              fill={slotA.color}
              stroke="#0E0E12"
              strokeWidth={1.5}
              style={{ cursor: 'pointer', transition: 'r 150ms ease' }}
              onMouseEnter={() => setHovered({ axis: i, slot: 'A' })}
              onMouseLeave={() => setHovered(null)}
            />
          ))}

        {/* Axis labels */}
        {RADAR_AXES.map((axis, i) => {
          const angle = -Math.PI / 2 + (i / AXIS_COUNT) * Math.PI * 2;
          const [lx, ly] = polarToXY(cx, cy, labelRadius, angle);
          const rawA = slotA ? axis.format(axis.rawValue(slotA.analytics)) : '—';
          const anchor = Math.abs(Math.cos(angle)) < 0.2 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
          return (
            <g
              key={axis.key}
              style={{ cursor: 'pointer' }}
              onClick={() => onAxisClick?.(axis.key)}
              className="hover:opacity-90"
            >
              <text
                x={lx}
                y={ly - 4}
                textAnchor={anchor}
                fill="rgba(255,255,255,0.85)"
                fontSize={12}
                fontWeight={500}
                fontFamily="AzbukaPro, sans-serif"
              >
                {axis.label}
              </text>
              <text
                x={lx}
                y={ly + 14}
                textAnchor={anchor}
                fill={slotA?.color ?? '#B3EC49'}
                fontSize={16}
                fontWeight={700}
                fontFamily="AzbukaPro, sans-serif"
              >
                {rawA}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Hover tooltip */}
      {hovered &&
        (() => {
          const axis = RADAR_AXES[hovered.axis];
          const slot = hovered.slot === 'A' ? slotA : slotB;
          if (!slot) return null;
          const [px, py] = hovered.slot === 'A' ? ptsA[hovered.axis] : ptsB[hovered.axis];
          return (
            <div
              className="absolute pointer-events-none rounded-lg shadow-xl px-3 py-2 text-xs"
              style={{
                left: px + 12,
                top: py - 12,
                background: '#1a1a1a',
                border: `1px solid ${slot.color}`,
                color: 'white',
                minWidth: 180,
                zIndex: 10,
              }}
            >
              <div className="font-semibold mb-0.5" style={{ color: slot.color }}>
                {slot.label}
              </div>
              <div className="text-greyP2">{axis.label}</div>
              <div className="text-white font-bold text-sm">{axis.format(axis.rawValue(slot.analytics))}</div>
              {axis.inverted && (
                <div className="mt-1 text-[10px] text-greyP2 italic">Inverted on radar (100 − gap)</div>
              )}
            </div>
          );
        })()}

      {/* Screen-reader accessible table */}
      <table className="sr-only">
        <caption>Radar chart data</caption>
        <thead>
          <tr>
            <th>Axis</th>
            <th>{slotA?.label ?? 'Slot A'}</th>
            <th>{slotB?.label ?? 'Slot B'}</th>
          </tr>
        </thead>
        <tbody>
          {RADAR_AXES.map((axis) => (
            <tr key={axis.key}>
              <td>{axis.label}</td>
              <td>{slotA ? axis.format(axis.rawValue(slotA.analytics)) : '—'}</td>
              <td>{slotB ? axis.format(axis.rawValue(slotB.analytics)) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
